import { useMemo } from 'react';
import { usePrerequisites } from '../contexts/Prerequisites';

const useCustomLocaleText = () => {
    const [{ strings }] = usePrerequisites();
    const localeText = useMemo(() => ({
        // Root
        rootGridLabel: 'grid',
        noRowsLabel: strings.NoRows || 'No rows',
        errorOverlayDefaultLabel: strings.Error || 'Error',

        // Columns selector toolbar button text
        toolbarColumns: strings.Columns || 'Columns...s',
        toolbarColumnsLabel: strings.SelectColumns || 'Select columns',

        // Columns panel text
        columnsPanelTextFieldLabel: strings.FindColumn || 'Find column',
        columnsPanelTextFieldPlaceholder: strings.ColumnTitle || 'Column title',
        columnsPanelDragIconLabel: 'Reorder column',
        columnsPanelShowAllButton: strings.ShowAll || 'Show all',
        columnsPanelHideAllButton: strings.HideAll || 'Hide all',

        // Filters toolbar button text
        toolbarFilters: strings.FilterText || 'Filters',
        toolbarFiltersLabel: strings.ShowFilters || 'Show filters',
        toolbarFiltersTooltipHide: strings.HideFilters || 'Hide filters',
        toolbarFiltersTooltipShow: strings.ShowFilters || 'Show filters',
        toolbarFiltersTooltipActive: (count) => (count !== 1 ? `${count} active filters` : `${count} active filter`),

        // Quick filter toolbar field
        toolbarQuickFilterPlaceholder: 'Search…',
        toolbarQuickFilterLabel: 'Search',
        toolbarQuickFilterDeleteIconLabel: 'Clear',

        // Export selector toolbar button text
        toolbarExport: strings.Export || 'Export',
        toolbarExportLabel: strings.Export || 'Export!!',
        toolbarExportCSV: 'Download as CSV',
        toolbarExportPrint: 'Print',
        toolbarExportExcel: 'Download as Excel',

        // Filter panel text
        filterPanelAddFilter: strings.AddFilter || 'Add filter',
        filterPanelRemoveAll: 'Remove all',
        filterPanelDeleteIconLabel: 'Delete',
        filterPanelLogicOperator: 'Logic operator',
        filterPanelOperator: strings.Operator || 'Operator',
        filterPanelOperatorAnd: 'And',
        filterPanelOperatorOr: 'Or',
        filterPanelColumns: strings.Columns || 'Columns',
        filterPanelInputLabel: strings.Value || 'Value',
        filterPanelInputPlaceholder: strings.FilterValue || 'Filter value',

        // Filter operators text
        filterOperatorContains: 'contains',
        filterOperatorEquals: strings.Equals || 'equals',
        filterOperatorStartsWith: 'starts with',
        filterOperatorEndsWith: 'ends with',
        filterOperatorIs: strings.Is || 'is',
        filterOperatorNot: strings.IsNot || 'is not',
        filterOperatorAfter: 'is after',
        filterOperatorOnOrAfter: 'is on or after',
        filterOperatorBefore: 'is before',
        filterOperatorOnOrBefore: 'is on or before',
        filterOperatorIsEmpty: strings.IsEmpty || 'is empty',
        filterOperatorIsNotEmpty: strings.IsNotEmpty || 'is not empty',
        filterOperatorIsAnyOf: strings.IsAnyOf || 'is any of',
        'filterOperator=': '=',
        'filterOperator!=': '!=',
        'filterOperator>': '>',
        'filterOperator>=': '>=',
        'filterOperator<': '<',
        'filterOperator<=': '<=',

        // Header filter operators text
        headerFilterOperatorContains: 'contains',
        headerFilterOperatorEquals: strings.Equals || 'Equals',
        headerFilterOperatorStartsWith: strings.StartsWith || 'Starts with',
        headerFilterOperatorEndsWith: strings.EndsWith || 'Ends with',
        headerFilterOperatorIs: strings.Is || 'Is',
        headerFilterOperatorNot: strings.IsNot || 'Is not',
        headerFilterOperatorAfter: strings.IsAfter || 'Is after',
        headerFilterOperatorOnOrAfter: strings.IsOnOrAfter || 'Is on or after',
        headerFilterOperatorBefore: strings.IsBefore || 'Is before',
        headerFilterOperatorOnOrBefore: strings.IsOnOrBefore || 'Is on or before',
        headerFilterOperatorIsEmpty: strings.IsEmpty || 'Is empty',
        headerFilterOperatorIsNotEmpty: strings.IsNotEmpty || 'Is not empty',
        headerFilterOperatorIsAnyOf: strings.IsAnyOf || 'Is any of',
        'headerFilterOperator=': strings.Equals || 'Equals',
        'headerFilterOperator!=': strings.NotEquals || 'Not equals',
        'headerFilterOperator>': strings.GreaterThan || 'Greater than',
        'headerFilterOperator>=': strings.GreaterThanOrEqualTo || 'Greater than or equal to',
        'headerFilterOperator<': strings.lessThan || 'Less than',
        'headerFilterOperator<=': strings.lessThanEqualTo || 'Less than or equal to',

        // Filter values text
        filterValueAny: 'any',
        filterValueTrue: 'true',
        filterValueFalse: 'false',

        // Column menu text
        columnMenuLabel: 'Menu',
        columnMenuShowColumns: 'Show columns',
        columnMenuManageColumns: 'Manage columns',
        columnMenuFilter: 'Filter',
        columnMenuHideColumn: 'Hide column',
        columnMenuUnsort: 'Unsort',
        columnMenuSortAsc: 'Sort by ASC',
        columnMenuSortDesc: 'Sort by DESC',

        // Column header text
        columnHeaderFiltersTooltipActive: (count) => (count !== 1 ? `${count} active filters` : `${count} active filter`),
        columnHeaderFiltersLabel: 'Show filters',
        columnHeaderSortIconLabel: 'Sort',

        // Rows selected footer text
        footerRowSelected: (count) =>
            count !== 1 ? `${count.toLocaleString()} rows selected` : `${count.toLocaleString()} row selected`,

        // Total row amount footer text
        footerTotalRows: 'Total Rows:',

        // Total visible row amount footer text
        footerTotalVisibleRows: (visibleCount, totalCount) =>
            `${visibleCount.toLocaleString()} of ${totalCount.toLocaleString()}`,

        // Checkbox selection text
        checkboxSelectionHeaderName: 'Checkbox selection',
        checkboxSelectionSelectAllRows: 'Select all rows',
        checkboxSelectionUnselectAllRows: 'Unselect all rows',
        checkboxSelectionSelectRow: 'Select row',
        checkboxSelectionUnselectRow: 'Unselect row',

        // Boolean cell text
        booleanCellTrueLabel: 'yes',
        booleanCellFalseLabel: 'no',

        // Actions cell more text
        actionsCellMore: 'more',

        // Column pinning text
        pinToLeft: 'Pin to left',
        pinToRight: 'Pin to right',
        unpin: 'Unpin',

        // Tree Data
        treeDataGroupingHeaderName: 'Group',
        treeDataExpand: 'see children',
        treeDataCollapse: 'hide children',

        // Grouping columns
        groupingColumnHeaderName: 'Group',
        groupColumn: (name) => `Group by ${name}`,
        unGroupColumn: (name) => `Stop grouping by ${name}`,

        // Master/detail
        detailPanelToggle: 'Detail panel toggle',
        expandDetailPanel: 'Expand',
        collapseDetailPanel: 'Collapse',

        // Used core components translation keys
        MuiTablePagination: {},

        // Row reordering text
        rowReorderingHeaderName: strings.Rowreordering || 'Row reordering',

        // Aggregation
        aggregationMenuItemHeader: 'Aggregation',
        aggregationFunctionLabelSum: 'sum',
        aggregationFunctionLabelAvg: 'avg',
        aggregationFunctionLabelMin: 'min',
        aggregationFunctionLabelMax: 'max',
        aggregationFunctionLabelSize: 'size',

		okButtonLabel: 'OK'
    }), [strings]);
    return localeText;
}
export default useCustomLocaleText